import { AttributeRule, AttributeRuleSelectionType, ProductFullAttribute } from 'API';
import { useOrderEntryContext } from 'providers/OrderModuleProvider';
import { useEffect, useMemo } from 'react';
import { useFetchCacheStore } from 'stores/useFetchCacheStore';
import { findAttributeCondition } from '../../AggregateDynamicItem/helper';
import { AttributeAggregateDynamicItem } from '../AggregateDynamicItem';
interface AggregateAttributeRuleProps {
  /**
   * The name of the attribute that is used to filter the attributes
   */
  attributeNameValue: string;
  /**
   * The attribute rule object that contains the display attributes
   */
  attributeRule: AttributeRule;
  /**
   * The selected tooth of the user
   */
  selectedTooth: string;
  /**
   * The product code of the product
   */
  productCode: string;
  /**
   * The order item id of the order item
   */
  orderItemId: string;
}

/**
 *  This component is responsible for rendering the rules of the attribute
 */
export const AggregateAttributeRule: React.FC<AggregateAttributeRuleProps> = ({
  attributeNameValue,
  attributeRule,
  selectedTooth,
  productCode,
  orderItemId,
}) => {
  const attributeValue = attributeRule.attributeValue;
  const selectionType = attributeRule.selectionType;

  const { order: localOrderData, updateOrderItem } = useOrderEntryContext();

  const selectedOrderItem = useMemo(() => {
    return localOrderData?.orderItems.find(item => item.id === orderItemId);
  }, [localOrderData?.orderItems, orderItemId]);

  const findProductFullAttributeByOption = useFetchCacheStore(state => state.findProductFullAttributeByOption);

  const mappedAttributes = useMemo(() => {
    const displayAttributes = attributeRule.displayAttributes || [];

    const attributes = displayAttributes.map(displayAttribute => {
      const { name } = displayAttribute;
      return findProductFullAttributeByOption(productCode, { name });
    });

    return attributes;
  }, [attributeRule.displayAttributes, findProductFullAttributeByOption, productCode]);

  /**
   * Filter the attributes based on the rule attribute value
   */
  const filteredDisplayAttributes = useMemo(() => {
    const attributes = mappedAttributes.filter((attribute): attribute is ProductFullAttribute => {
      // if the attribute is undefined, don't display it
      if (!attribute) return false;

      // if the rule attribute value is empty, display all attributes
      if (!attributeValue) return true;

      // if the attribute name value is equal to the rule attribute value, display the attribute otherwise don't display it
      return attributeNameValue === attributeValue;
    });

    return attributes;
  }, [attributeNameValue, attributeValue, mappedAttributes]);

  /**
   * Check if the selected order item has any implant system value
   * if the selection type is RequireOne, check if the selected order item has any implant system value
   * if the selection type is not RequireOne, return false
   */
  const isSelectedSomeImplantSystemValue = useMemo(() => {
    // if selectionType is not RequireOne, return false
    if (selectionType !== AttributeRuleSelectionType.RequireOne) return false;

    // check if the selected order item has any implant system value
    const isSelectedSomeImplantSystem = filteredDisplayAttributes.some(displayAttribute => {
      const { name } = displayAttribute;
      const selectedAttributeCondition = findAttributeCondition(selectedOrderItem, name, selectedTooth);
      return !!selectedAttributeCondition?.name;
    });

    return isSelectedSomeImplantSystem;
  }, [filteredDisplayAttributes, selectedOrderItem, selectedTooth, selectionType]);

  /**
   * this is used to show the display attributes
   * if the selection type is RequireOne, show the display attributes that are selected
   * if the selection type is not RequireOne, show all filtered display attributes
   */
  const showingDisplayAttributes = useMemo(() => {
    if (!isSelectedSomeImplantSystemValue) return filteredDisplayAttributes;

    return filteredDisplayAttributes.filter(displayAttribute => {
      const { name } = displayAttribute;
      const selectedAttributeCondition = findAttributeCondition(selectedOrderItem, name, selectedTooth);
      return !!selectedAttributeCondition?.name;
    });
  }, [filteredDisplayAttributes, isSelectedSomeImplantSystemValue, selectedOrderItem, selectedTooth]);

  /**
   * If selectedDisplayAttributes is empty, return an empty array
   * if selectedDisplayAttributes is not empty, filter the attributes based on the selected required attributes
   * this way we can know what is required attribute and what is not required attribute
   */
  const selectedDisplayAttributes = useMemo(() => {
    // if the selection type is not RequireOne, return an empty array
    if (selectionType !== AttributeRuleSelectionType.RequireOne) return [];

    /**
     * Filter the attributes based on the selected order item and selected tooth
     */
    const selectedAttributes = filteredDisplayAttributes.filter(displayAttribute => {
      const { name } = displayAttribute;
      const selectedAttributeCondition = findAttributeCondition(selectedOrderItem, name, selectedTooth);
      return !!selectedAttributeCondition?.name;
    });
    return selectedAttributes;
  }, [filteredDisplayAttributes, selectedOrderItem, selectedTooth, selectionType]);

  /**
   * Update the selected order item based on whether order item attribute is invalid or not
   * if selectedDisplayAttributes is empty or selectedOrderItem is undefined, return
   */
  useEffect(() => {
    // if selectedDisplayAttributes is empty or selectedOrderItem is undefined, return
    if (!selectedDisplayAttributes.length || !selectedOrderItem) return;

    filteredDisplayAttributes.forEach(displayAttribute => {
      const { name } = displayAttribute;
      // check if the selectedDisplayAttributes has the name of the display attribute
      const isExist = selectedDisplayAttributes.some(item => item.type === name);
      if (isExist) return; // if it exists, don't update the order item
      // if it doesn't exist, check if the selected order item has the attribute condition
      const selectedAttributeCondition = findAttributeCondition(selectedOrderItem, name, selectedTooth);
      if (!selectedAttributeCondition) return;
      // if the selected attribute condition is invalid, update the order item
      // set the isInvalid to false
      // and update the attributes of the selected order item
      if (selectedAttributeCondition.isInvalid) {
        selectedAttributeCondition.isInvalid = false;
        updateOrderItem(selectedOrderItem.id, {
          attributes: [...selectedOrderItem.attributes],
        });
      }
    });
  }, [selectedDisplayAttributes, filteredDisplayAttributes, selectedOrderItem, selectedTooth, updateOrderItem]);

  return (
    <>
      {showingDisplayAttributes.map(displayAttribute => (
        <AttributeAggregateDynamicItem
          key={displayAttribute.name}
          {...displayAttribute}
          selectedTooth={selectedTooth}
          localTypeValue={displayAttribute.name}
          orderItemId={orderItemId}
        />
      ))}
    </>
  );
};
